<template>
  <NiceView
    :aboveTitle="generalStore.viewHeader"
    :title="CommonService.routes.CHECKIN_LOGS.title"
    :icon="CommonService.routes.CHECKIN_LOGS.icon"
    :loading="loading"
    flexBody
  >
    <!-- Filters -->
    <NiceFilters v-model="filtersList" @change="filterChanged" :showCreateButton="false">
      <!-- Refresh -->
      <NiceButton icon="icon-refresh-cw" :title="$gettext('Refresh')" @click="getData" plain :loading="loading"></NiceButton>
    </NiceFilters>

    <!-- Table -->
    <NiceTable :actions="actions" :columns="columns" :data="data" :loading="loading" showFooter showLimit paginated @filterChange="filterChanged" />

    <!-- Form -->
    <NicePanel :name="panelName" @close="closeEdit">
      <CheckinLogForm @cancel="closeEdit" @success="successEdit" :data="selectedItem"></CheckinLogForm>
    </NicePanel>

    <!-- PDF Viewier -->
    <PDFViewerModal :blob="pdfBlob" :loading="pdfBlobLoading" width="95vw" height="95vh"></PDFViewerModal>

    <!-- <pre>{{ data }}</pre> -->
  </NiceView>
</template>

<script setup>
import { ref, inject, watch, onMounted } from "vue";

import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import ApiService from "@/services/api/api.service";
import PDFViewerModal from "@/components/modals/PDFViewerModal.vue";
import CheckinLogForm from "@/components/forms/CheckinLogForm.vue";

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { formatDateWithTime } from "@/plugins/date";
const $getQuery = inject("getQuery");
const $nice = inject("nice");

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const modules = ref(generalStore.modules);

const data = ref([]);
const loading = ref(false);
const selectedItem = ref(null);
const pdfBlob = ref(null);
const pdfBlobLoading = ref(false);
const panelName = "event";
const organizationId = route.params.organizationId;
const eventId = route.params.eventId;

// Watch for id change
watch(
  () => route.query,
  () => {
    if (route.query.id) {
      $nice.panel(panelName, true);
    }
  }
);

onMounted(async () => {
  if (route.query.id) {
    $nice.panel(panelName, true);
  }
});

function closeEdit() {
  selectedItem.value = null;
  router.push({ query: { ...route.query, id: undefined } });
  $nice.panel(panelName, false);
}

function successEdit() {
  closeEdit();
  getData();
}

function openEdit(item) {
  selectedItem.value = item;
  router.push({ query: { ...route.query, id: item.id } });
}

async function getData() {
  loading.value = true;
  try {
    const query = await $getQuery();
    query.event = route.params.eventId;
    data.value = await ApiService.checkinLogs.getAll(route.params.organizationId, query);
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

const filtersList = [
  {
    name: $gettext("Status"),
    type: "select",
    key: "status",
    valueName: "name",
    searchFunction: searchStatuses,
  },
  {
    name: $gettext("Guest"),
    type: "select",
    key: "guest",
    formatter: (v) => `
      <div>${v.first_name} ${v.last_name}</div>
      ${v.company_name ? `<small>${v.company_name}</small>` : ""}
    `,
    searchFunction: searchGuests,
  },
  {
    name: $gettext("Event"),
    type: "select",
    key: "event",
    valueName: "name",
    searchFunction: searchEvents,
  },
  // {
  //   name: $gettext("After"),
  //   type: "date",
  //   key: "timestamp_after",
  // },
  // {
  //   name: $gettext("Before"),
  //   type: "date",
  //   key: "timestamp_before",
  // },
  // checked_in_by
];

function filterChanged() {
  getData();
}

async function searchStatuses() {
  return CommonService.statuses;
}

async function searchGuests(keyword) {
  return ApiService.guests.getAll(organizationId, eventId, { search: keyword });
}

async function searchEvents(keyword) {
  return ApiService.events.getAll(organizationId, { search: keyword });
}

const actions = [
  // {
  //   icon: () => "icon-eye",
  //   function: (v) => openEdit(v),
  // },
];

const columns = [
  {
    name: $gettext("Guest"),
    key: "guest",
    html: (v) => `
      <div>${v.first_name} ${v.last_name}</div>
      ${v.company_name ? `<small>${v.company_name}</small>` : ""}
    `,
    hasOrder: true,
  },
  {
    name: $gettext("Status"),
    key: "status",
    class: "text-center",
    html: (v) => `<span class="badge ${CommonService.statusesMap[v].class}">${CommonService.statusesMap[v].name}</span>`,
    hasOrder: true,
  },
  {
    name: $gettext("Error"),
    key: "error_code",
    html: (v, row) => (v ? `<small>${v}</small>` : "") + (row.error_message ? `<div>${row.error_message}</div>` : ""),
    hasOrder: true,
  },
  {
    name: $gettext("Checked in by"),
    key: "checked_in_by",
    html: (v) => (v ? `<small>${v.email}</small><div>${v.first_name || "-"} ${v.last_name || "-"}</div>` : ""),
    hasOrder: true,
  },
  {
    name: $gettext("Checked in +1"),
    key: "checked_in_plus_one_count",
    class: "text-right",
    disabled: !modules.value.PLUS_ONES,
    hasOrder: true,
  },
  {
    name: $gettext("Timestamp"),
    key: "timestamp",
    class: "text-right",
    formatter: (v) => formatDateWithTime(v),
    hasOrder: true,
  },
];
</script>
